import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-msal-loading',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="splash" [class.fade-out]="fading">

      <div class="splash-body">

        <!-- Brand -->
        <div class="brand">
          <img class="brand-logo"
               src="https://cdaraucania.sfo2.cdn.digitaloceanspaces.com/01.resources/02.cdaraucania/01.img/da-logo.png"
               alt="Desarrolla Araucanía" />
          <div class="brand-name">Desarrolla Araucanía</div>
          <div class="brand-sub">Portal Corporativo</div>
        </div>

        <div class="divider"></div>

        <!-- Error State -->
        <div class="error-pane" *ngIf="hasError">
          <div class="error-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
          </div>
          <h3 class="error-title">No se pudo inicializar</h3>
          <p class="error-msg">
            Ocurrió un error al conectar con el sistema de autenticación.<br>
            Verifica tu conexión a internet e intenta nuevamente.
          </p>
          <div class="error-actions">
            <button class="btn-primary" (click)="retryClicked.emit()">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              Reintentar
            </button>
            <button class="btn-ghost" (click)="clearAndReload()">
              Limpiar caché y recargar
            </button>
          </div>
        </div>

        <!-- Loading Steps -->
        <div class="steps" *ngIf="!hasError">
          <div class="step"
               *ngFor="let s of steps; let i = index"
               [class.step--pending]="stepIndex < i"
               [class.step--active]="stepIndex === i"
               [class.step--done]="stepIndex > i">
            <div class="step-icon">
              <!-- Spinner (active) -->
              <div class="step-spinner" *ngIf="stepIndex === i"></div>
              <!-- Check (done) -->
              <svg class="step-check" *ngIf="stepIndex > i" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              <!-- Dot (pending) -->
              <div class="step-dot" *ngIf="stepIndex < i"></div>
            </div>
            <span class="step-label">{{ s }}</span>
          </div>
        </div>

      </div>

      <div class="splash-footer"></div>
    </div>
  `,
  styles: [`
    :host { display: block; }

    /* ── Splash wrapper ── */
    .splash {
      position: fixed; inset: 0;
      background: #ffffff;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      z-index: 9999;
      transition: opacity 0.5s ease, transform 0.5s ease;
    }
    .splash.fade-out {
      opacity: 0;
      transform: scale(1.015);
      pointer-events: none;
    }

    /* ── Body ── */
    .splash-body {
      display: flex; flex-direction: column; align-items: center;
      width: 100%; max-width: 360px; padding: 0 32px;
    }

    /* ── Brand ── */
    .brand {
      display: flex; flex-direction: column; align-items: center;
      gap: 10px; margin-bottom: 28px;
    }
    .brand-logo {
      width: 72px; height: 72px; object-fit: contain;
      filter: drop-shadow(0 4px 16px rgba(255,107,53,.18));
    }
    .brand-name {
      font-size: 1.45rem; font-weight: 800; color: #1e2a3a;
      letter-spacing: -0.015em; line-height: 1;
    }
    .brand-sub {
      font-size: 0.72rem; font-weight: 600; color: #94a3b8;
      letter-spacing: 0.12em; text-transform: uppercase;
    }

    /* ── Divider ── */
    .divider {
      width: 36px; height: 3px;
      background: #ff6b35; border-radius: 2px;
      margin-bottom: 28px;
    }

    /* ── Steps ── */
    .steps { display: flex; flex-direction: column; gap: 18px; width: 100%; }

    .step {
      display: flex; align-items: center; gap: 14px;
      transition: opacity 0.3s;
    }
    .step--pending { opacity: 0.28; }
    .step--active  { opacity: 1; }
    .step--done    { opacity: 0.6; }

    .step-icon {
      width: 28px; height: 28px; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center;
    }
    .step-spinner {
      width: 28px; height: 28px; border-radius: 50%;
      border: 2.5px solid rgba(255,107,53,.18);
      border-top-color: #ff6b35;
      animation: spin .8s linear infinite;
    }
    .step-check {
      width: 20px; height: 20px; color: #10b981;
    }
    .step-dot {
      width: 9px; height: 9px; border-radius: 50%;
      background: #cbd5e1; margin: auto;
    }
    .step-label {
      font-size: 0.9rem; font-weight: 500; color: #4b5563;
    }
    .step--active .step-label  { color: #1e2a3a; font-weight: 700; }
    .step--done   .step-label  { color: #10b981; }

    @keyframes spin { to { transform: rotate(360deg); } }

    /* ── Error ── */
    .error-pane {
      display: flex; flex-direction: column;
      align-items: center; text-align: center; gap: 8px;
      width: 100%;
    }
    .error-icon {
      width: 52px; height: 52px; color: #ef4444; margin-bottom: 4px;
    }
    .error-icon svg { width: 100%; height: 100%; }
    .error-title {
      font-size: 1.05rem; font-weight: 800; color: #1e2a3a; margin: 0;
    }
    .error-msg {
      font-size: 0.83rem; color: #64748b;
      line-height: 1.65; margin: 0;
    }
    .error-actions {
      display: flex; flex-direction: column;
      gap: 10px; width: 100%; margin-top: 10px;
    }
    .btn-primary {
      display: inline-flex; align-items: center;
      justify-content: center; gap: 8px;
      padding: 11px 20px; background: #ff6b35; color: #fff;
      border: none; border-radius: 10px;
      font-size: 0.9rem; font-weight: 700;
      cursor: pointer; transition: background .2s; width: 100%;
    }
    .btn-primary svg { width: 15px; height: 15px; }
    .btn-primary:hover { background: #e85d28; }
    .btn-ghost {
      background: none; border: none;
      color: #94a3b8; font-size: 0.78rem;
      text-decoration: underline; cursor: pointer;
      padding: 2px;
    }
    .btn-ghost:hover { color: #64748b; }

    /* ── Bottom accent strip ── */
    .splash-footer {
      position: absolute; bottom: 0; left: 0; right: 0;
      height: 4px;
      background: linear-gradient(90deg, #ff6b35 0%, #ffaa80 100%);
    }
  `]
})
export class MsalLoadingComponent implements OnChanges {
  @Input() phase: 'init' | 'auth' | 'permissions' | 'done' = 'init';
  @Input() hasError = false;
  @Input() isReady = false;
  @Output() fadeComplete = new EventEmitter<void>();
  @Output() retryClicked = new EventEmitter<void>();

  fading = false;

  readonly steps = [
    'Sistema de autenticación',
    'Verificando sesión',
    'Cargando permisos'
  ];

  get stepIndex(): number {
    if (this.phase === 'init') return 0;
    if (this.phase === 'auth') return 1;
    if (this.phase === 'permissions') return 2;
    return 3;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isReady']?.currentValue === true && !this.hasError) {
      this.fading = true;
      setTimeout(() => this.fadeComplete.emit(), 520);
    }
  }

  clearAndReload(): void {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  }
}
